
  import { defineComponent, onMounted, ref, computed } from 'vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import moment from 'moment';
  import Multiselect from '@vueform/multiselect';
  import XLSX from 'xlsx-js-style';

  interface AuctionReportItem {
    organiserId: number;
    organiserName: string;
    propertyTypeId: number;
    propertyTypeName: string;
    auctionEventsCount: number;
    listedPropertiesCount: number;
    soldPropertiesCount: number;
    startingPriceAll: number;
    startingPriceSold: number;
    salePriceTotal: number;
    percentOfSold: number;
    increasePercentSold: number;
  }

  export default defineComponent({
    name: 'auctions-report',
    components: { Multiselect },
    async setup() {
      const showExportBtn = ref(false);
      const data = ref<{ data: Array<AuctionReportItem> } | null>(null);
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();

      const selectedItems = ref<{
        dateFrom: string;
        dateTo: string;
        propertyTypeIds: number[];
        areaIds: number[];
        organiserIds: number[];
        onlyFullySold: boolean;
      }>({
        dateFrom: '',
        dateTo: '',
        propertyTypeIds: [],
        areaIds: [],
        organiserIds: [],
        onlyFullySold: false,
      });

      const translate = (text: string): string => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      // Form validation - dates are required
      const isFormValid = computed(() => {
        return (
          selectedItems.value.dateFrom !== '' &&
          selectedItems.value.dateTo !== ''
        );
      });

      // Generate Excel filename with date range
      const excelFileName = computed(() => {
        const dateFrom = selectedItems.value.dateFrom
          ? moment(selectedItems.value.dateFrom).format('YYYY-MM-DD')
          : '';
        const dateTo = selectedItems.value.dateTo
          ? moment(selectedItems.value.dateTo).format('YYYY-MM-DD')
          : '';
        return `إحصائية مزادات ${dateFrom} - ${dateTo}.xlsx`;
      });

      // Format percentage (English numerals)
      const formatPercent = (num: number): string => {
        if (num === null || num === undefined) return '0%';
        return `${num.toFixed(2)}%`;
      };

      // DDL computed properties (needed for export filter titles)
      const areas = computed(() => store.getters.allListAreas || []);
      const organizers = computed(
        () => store.getters.auctionOrganisesList || []
      );
      const propertyTypes = computed(
        () => store.getters.allAuctionPropertyTypes || []
      );

      // Export to Excel with RTL, merged cells and styling
      const exportToExcel = () => {
        if (!data.value || !data.value.data || data.value.data.length === 0) {
          return;
        }

        const reportData = data.value.data;

        // Group data by organizer
        const groupedData: Map<
          number,
          {
            organiserName: string;
            auctionEventsCount: number;
            items: AuctionReportItem[];
          }
        > = new Map();

        reportData.forEach((item) => {
          if (!groupedData.has(item.organiserId)) {
            groupedData.set(item.organiserId, {
              organiserName: item.organiserName,
              auctionEventsCount: item.auctionEventsCount,
              items: [],
            });
          }
          groupedData.get(item.organiserId)!.items.push(item);
        });

        // Build filter title lines
        const filterLines: string[] = [];

        // Date range
        filterLines.push(
          `الفترة: من ${selectedItems.value.dateFrom} إلى ${selectedItems.value.dateTo}`
        );

        // Property categories
        if (selectedItems.value.propertyTypeIds.length > 0) {
          const selectedIds = selectedItems.value.propertyTypeIds.map((id) =>
            Number(id)
          );
          const selectedTypes = propertyTypes.value
            .filter((t) => selectedIds.includes(Number(t.id)))
            .map((t) => t.title || t.titleAr || t.name || '')
            .filter((name) => name)
            .join('، ');
          if (selectedTypes) {
            filterLines.push(`الفئات: ${selectedTypes}`);
          }
        }

        // Areas
        if (selectedItems.value.areaIds.length > 0) {
          const selectedAreas = areas.value
            .filter((a) => selectedItems.value.areaIds.includes(a.id))
            .map((a) => a.titleAr || a.title || a.name || '')
            .filter((name) => name)
            .join('، ');
          if (selectedAreas) {
            filterLines.push(`المناطق: ${selectedAreas}`);
          }
        }

        // Organizers
        if (selectedItems.value.organiserIds.length > 0) {
          const selectedOrgs = organizers.value
            .filter((o) => selectedItems.value.organiserIds.includes(o.id))
            .map((o) => o.titleAr || o.title || o.name || '')
            .filter((name) => name)
            .join('، ');
          if (selectedOrgs) {
            filterLines.push(`المنظمين: ${selectedOrgs}`);
          }
        }

        // Only fully sold
        if (selectedItems.value.onlyFullySold) {
          filterLines.push('نسبة البيع: 100% فقط');
        }

        // Headers (RTL: index 0 = rightmost column)
        const headers = [
          'منظم المزاد',
          'عدد المزادات',
          'فئة العقار',
          'عدد العقارات المعروضة',
          'سعر الإبتداء للعقارات المعروضة',
          'عدد العقارات المباعة',
          'سعر الإبتداء للعقارات المباعة',
          'سعر البيع',
          'نسبة البيع من المعروض',
          'نسبة الزيادة للعقارات المباعة',
        ];

        // Common border style
        const border = {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } },
        };

        // Cell styles
        const headerStyle = {
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
          fill: { patternType: 'solid', fgColor: { rgb: '4472C4' } },
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
          },
          border,
        };

        const organiserStyle = {
          font: { bold: true, sz: 11 },
          fill: { patternType: 'solid', fgColor: { rgb: 'D9E2F3' } },
          alignment: { horizontal: 'center', vertical: 'center' },
          border,
        };

        const dataStyle = {
          font: { sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border,
        };

        const numberStyle = {
          font: { sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border,
          numFmt: '#,##0',
        };

        // Title style
        const titleStyle = {
          font: { bold: true, sz: 14, color: { rgb: '1F4E79' } },
          alignment: { horizontal: 'right', vertical: 'center' },
        };

        const filterStyle = {
          font: { sz: 11, color: { rgb: '595959' } },
          alignment: { horizontal: 'right', vertical: 'center' },
        };

        // Build worksheet data
        type CellStyle =
          | typeof headerStyle
          | typeof dataStyle
          | typeof numberStyle
          | typeof organiserStyle
          | typeof titleStyle
          | typeof filterStyle;
        const wsData: { v: string | number; s: CellStyle }[][] = [];
        const merges: XLSX.Range[] = [];

        // Add main title row
        const titleRow: { v: string | number; s: CellStyle }[] = Array(10)
          .fill(null)
          .map(() => ({ v: '', s: titleStyle }));
        titleRow[0] = { v: 'تقرير إحصائيات المزادات', s: titleStyle };
        wsData.push(titleRow);
        // Merge title across all columns
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } });

        // Add filter lines
        filterLines.forEach((line, idx) => {
          const filterRow: { v: string | number; s: CellStyle }[] = Array(10)
            .fill(null)
            .map(() => ({ v: '', s: filterStyle }));
          filterRow[0] = { v: line, s: filterStyle };
          wsData.push(filterRow);
          // Merge filter row across all columns
          merges.push({ s: { r: idx + 1, c: 0 }, e: { r: idx + 1, c: 9 } });
        });

        // Add empty row before headers
        wsData.push(
          Array(10)
            .fill(null)
            .map(() => ({ v: '', s: dataStyle }))
        );

        // Calculate header row index
        const headerRowIndex = filterLines.length + 2; // title + filters + empty row

        // Add header row with style
        wsData.push(headers.map((h) => ({ v: h, s: headerStyle })));

        let currentRow = headerRowIndex + 1; // Start after header row

        // Process each organizer group
        groupedData.forEach((group) => {
          const startRow = currentRow;
          const rowCount = group.items.length;

          group.items.forEach((item, index) => {
            const row = [
              {
                v: index === 0 ? group.organiserName : '',
                s: organiserStyle,
              },
              {
                v: index === 0 ? group.auctionEventsCount : '',
                s: organiserStyle,
              },
              { v: item.propertyTypeName, s: dataStyle },
              { v: item.listedPropertiesCount, s: numberStyle },
              { v: item.startingPriceAll, s: numberStyle },
              { v: item.soldPropertiesCount, s: numberStyle },
              { v: item.startingPriceSold, s: numberStyle },
              { v: item.salePriceTotal, s: numberStyle },
              { v: formatPercent(item.percentOfSold), s: dataStyle },
              { v: formatPercent(item.increasePercentSold), s: dataStyle },
            ];
            wsData.push(row);
            currentRow++;
          });

          // Merge cells for organizer name and auction count
          if (rowCount > 1) {
            merges.push({
              s: { r: startRow, c: 0 },
              e: { r: startRow + rowCount - 1, c: 0 },
            });
            merges.push({
              s: { r: startRow, c: 1 },
              e: { r: startRow + rowCount - 1, c: 1 },
            });
          }
        });

        // Create worksheet from array of arrays with styles
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Apply merges
        ws['!merges'] = merges;

        // Set column widths
        ws['!cols'] = [
          { wch: 25 }, // منظم المزاد
          { wch: 14 }, // عدد المزادات
          { wch: 15 }, // فئة العقار
          { wch: 22 }, // عدد العقارات المعروضة
          { wch: 26 }, // سعر الإبتداء للعقارات المعروضة
          { wch: 20 }, // عدد العقارات المباعة
          { wch: 26 }, // سعر الإبتداء للعقارات المباعة
          { wch: 15 }, // سعر البيع
          { wch: 26 }, // نسبة الزيادة للعقارات المعروضة
          { wch: 26 }, // نسبة الزيادة للعقارات المباعةنسبة البيع من المعروض
        ];

        // Set row heights (header row taller)
        ws['!rows'] = [{ hpt: 35 }];

        // Create workbook
        const wb = XLSX.utils.book_new();

        // Set RTL for the sheet
        if (!wb.Workbook) wb.Workbook = {};
        if (!wb.Workbook.Views) wb.Workbook.Views = [];
        wb.Workbook.Views[0] = { RTL: true };

        XLSX.utils.book_append_sheet(wb, ws, 'تقرير المزادات');

        // Generate and download the file
        XLSX.writeFile(wb, excelFileName.value);
      };

      const onSubmitCreate = async () => {
        // Validate dates are provided
        if (!selectedItems.value.dateFrom || !selectedItems.value.dateTo) {
          Swal.fire({
            text: translate('dateRangeRequired'),
            icon: 'warning',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-warning',
            },
          });
          return;
        }

        // Validate dateTo >= dateFrom
        const fromDate = moment(selectedItems.value.dateFrom);
        const toDate = moment(selectedItems.value.dateTo);
        if (toDate.isBefore(fromDate)) {
          Swal.fire({
            text: translate('dateToMustBeAfterDateFrom'),
            icon: 'warning',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-warning',
            },
          });
          return;
        }

        // Create formatted dates without mutating original values
        const formattedParams = {
          value: {
            ...selectedItems.value,
            dateFrom: fromDate.format('YYYY-MM-DD'),
            dateTo: toDate.format('YYYY-MM-DD'),
          },
        };

        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        try {
          await store.dispatch(Actions.GET_AUCTION_REPORT, formattedParams);
          data.value = store.getters.auctionReportData;

          if (data.value && data.value.data && data.value.data.length > 0) {
            showExportBtn.value = true;
            Swal.fire({
              text: translate('SUCCESSFULLY_GET_REPORTS_DATA'),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          } else {
            showExportBtn.value = false;
            Swal.fire({
              text: translate('noDataFound'),
              icon: 'info',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-info',
              },
            });
          }
        } catch (error) {
          const [errorName] = Object.keys(store.getters.getErrors);
          const errorMsg = store.getters.getErrors[errorName];

          Swal.fire({
            text: translate(errorMsg?.[0] || 'errorOccurred'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } finally {
          submitButton.value?.removeAttribute('data-kt-indicator');
          if (submitButton.value) {
            submitButton.value.disabled = false;
          }
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('auctionsReport'), [
          translate('Reports'),
        ]);
      });

      // Load DDL data
      await store.dispatch(Actions.GET_ALL_AREAS_LIST, { type: 'auction' });
      await store.dispatch(Actions.GET_ALL_AUCTION_ORGANISERS);
      await store.dispatch(Actions.GET_ALL_AUCTION_PROPERTY_TYPES);

      reinitializeComponents();
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        areas,
        organizers,
        propertyTypes,
        excelFileName,
        showExportBtn,
        data,
        selectedItems,
        onSubmitCreate,
        exportToExcel,
        submitButton,
        translate,
        goBack,
        can,
        isFormValid,
      };
    },
  });
